import {
  CLEAR_CUSTOMER_FORM_STATE,
  CREATE_UPDATE_CUSTOMER_FAIL,
  CREATE_UPDATE_CUSTOMER_START,
  CREATE_UPDATE_CUSTOMER_SUCCESS,
  GET_CUSTOMER_PAYMENT_METHODS_FAIL,
  GET_CUSTOMER_PAYMENT_METHODS_START,
  GET_CUSTOMER_PAYMENT_METHODS_SUCCESS,
  GET_CUSTOMER_TOKENIZATION_PAGE_FAIL,
  GET_CUSTOMER_TOKENIZATION_PAGE_START,
  GET_CUSTOMER_TOKENIZATION_PAGE_SUCCESS,
  SET_CUSTOMER_FORM_FIELD_REQUIRED_MAPPING,
  SET_PARSED_CUSTOMER_FIELDS,
  SET_DEFAULT_CARD_SUCCESS,
  SET_DEFAULT_CARD_FAIL,
  SET_DEFAULT_CARD_START,
  SET_DEFAULT_CARD_END,
  DELETE_CARD_START,
  DELETE_CARD_FAIL,
  SET_EDIT_CARD_EXPANDED,
  EDIT_CARD_START,
  EDIT_CARD_SUCCESS,
  EDIT_CARD_FAIL,
  DELETE_CARD_SUCCESS
} from 'redux/constants/customer';
import { cloneDeep } from 'lodash-es';
import { initiateCustomerFields } from 'utilities/customer';
import * as reduxUtil from 'redux/utils/reducers';
import { IAction, ICategory } from 'models';
import { GET_CHECKOUT_PAGE_SUCCESS } from 'redux/constants/checkout';
import { GET_SUBSCRIPTION_PAGE_SUCCESS } from 'redux/constants/subscription';
import { CATEGORIES } from 'consts';

const stateKeys = {
  form: 'form',
  page: 'page',
  createUpdateCustomer: 'createUpdateCustomer'
};

const INITIAL_FORM_OBJECT = {
  requiredMapping: initiateCustomerFields({
    required: false,
    hidden: true,
    skipDefaultValidation: false,
    description: '',
    regex: ''
  }),
  parsedCustomerFields: {}
};

const initialState = {
  customerToken: '',
  pagePaymentMethodsTypes: { card: [], bank: [] },
  customerPaymentMethods: {
    data: {
      card: [],
      bank: []
    },
    loading: false,
    error: ''
  },
  defaultPm: {
    id: '',
    loading: false,
    error: ''
  },
  editFormExpanded: false,
  editCardAction: reduxUtil.initializeObject(),
  deleteCardAction: reduxUtil.initializeObject(),
  form: INITIAL_FORM_OBJECT,
  page: reduxUtil.initializeObject(),
  createUpdateCustomer: reduxUtil.initializeObject()
};

export type ICustomerStore = typeof initialState;

const reducer = (state = initialState, action: IAction<string, any>) => {
  const { data, error } = action?.payload || {};
  const { page } = state || {};
  const { data: currentPageData } = page || {};
  switch (action.type) {
    case GET_CUSTOMER_TOKENIZATION_PAGE_START:
      return reduxUtil.updateStartAction({ state, key: stateKeys.page });
    case GET_CUSTOMER_TOKENIZATION_PAGE_SUCCESS:
      return reduxUtil.updateSuccessAction({
        state,
        key: stateKeys.page,
        data
      });
    case GET_CUSTOMER_TOKENIZATION_PAGE_FAIL:
      return reduxUtil.updateFailAction({ state, key: stateKeys.page, error });
    case CREATE_UPDATE_CUSTOMER_START:
      return reduxUtil.updateStartAction({
        state,
        key: stateKeys.createUpdateCustomer
      });
    case CREATE_UPDATE_CUSTOMER_SUCCESS: {
      const { updated_page: updatedPage, customer } = action.payload.data;
      const { status, customer: customerToken } = updatedPage || {};
      const stateWithUpdatedPage = {
        ...state,
        ...reduxUtil.updateKeyDataState({
          state,
          key: stateKeys.page,
          data: {
            ...currentPageData,
            customer: customerToken,
            status
          }
        })
      };
      return reduxUtil.updateSuccessAction({
        state: stateWithUpdatedPage,
        key: stateKeys.createUpdateCustomer,
        data: customer
      });
    }
    case CREATE_UPDATE_CUSTOMER_FAIL:
      return reduxUtil.updateFailAction({
        state,
        key: stateKeys.createUpdateCustomer,
        error
      });
    case SET_CUSTOMER_FORM_FIELD_REQUIRED_MAPPING: {
      const requiredMapping = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          requiredMapping: {
            ...(state.form as typeof INITIAL_FORM_OBJECT).requiredMapping,
            ...requiredMapping
          }
        }
      };
    }
    case CLEAR_CUSTOMER_FORM_STATE: {
      return {
        ...state,
        form: cloneDeep(INITIAL_FORM_OBJECT)
      };
    }
    case SET_PARSED_CUSTOMER_FIELDS: {
      const parsedCustomerFields = action.payload;
      return {
        ...state,
        form: {
          ...state.form,
          parsedCustomerFields
        }
      };
    }
    case GET_CUSTOMER_PAYMENT_METHODS_START: {
      return {
        ...state,
        customerPaymentMethods: {
          ...state.customerPaymentMethods,
          loading: true
        }
      };
    }
    case GET_CUSTOMER_PAYMENT_METHODS_SUCCESS: {
      const { data, defaultPmId, customerToken } = action.payload;
      return {
        ...state,
        customerToken,
        customerPaymentMethods: {
          data: {
            ...state.customerPaymentMethods.data,
            ...data
          },
          loading: false,
          error: ''
        },
        defaultPm: {
          id: defaultPmId,
          loading: false,
          error: ''
        }
      };
    }
    case GET_CUSTOMER_PAYMENT_METHODS_FAIL: {
      const { error } = action.payload;
      return {
        ...state,
        customerPaymentMethods: {
          ...state.customerPaymentMethods,
          loading: false,
          error
        }
      };
    }
    case SET_EDIT_CARD_EXPANDED: {
      const { expanded } = action.payload;
      return {
        ...state,
        editFormExpanded: expanded
      };
    }
    case EDIT_CARD_START: {
      return {
        ...state,
        editCardAction: {
          ...state.editCardAction,
          loading: true
        }
      };
    }
    case EDIT_CARD_SUCCESS: {
      return {
        ...state,
        editCardAction: {
          ...state.editCardAction,
          loading: false
        }
      };
    }
    case EDIT_CARD_FAIL: {
      const { error } = action.payload;
      return {
        ...state,
        editCardAction: {
          ...state.editCardAction,
          loading: false,
          error
        }
      };
    }
    case DELETE_CARD_START: {
      return {
        ...state,
        deleteCardAction: {
          ...state.editCardAction,
          loading: true
        }
      };
    }
    case DELETE_CARD_SUCCESS: {
      return {
        ...state,
        deleteCardAction: {
          ...state.editCardAction,
          loading: false
        }
      };
    }
    case DELETE_CARD_FAIL: {
      const { error } = action.payload;
      return {
        ...state,
        deleteCardAction: {
          ...state.editCardAction,
          loading: false,
          error
        }
      };
    }
    case SET_DEFAULT_CARD_START: {
      return {
        ...state,
        defaultPm: {
          ...state.defaultPm,
          loading: true
        }
      };
    }
    case SET_DEFAULT_CARD_SUCCESS: {
      const { defaultPmId } = action.payload;
      return {
        ...state,
        defaultPm: {
          ...state.defaultPm,
          id: defaultPmId,
          loading: false,
          error: ''
        }
      };
    }
    case SET_DEFAULT_CARD_FAIL: {
      const { error } = action.payload;
      return {
        ...state,
        defaultPm: {
          ...state.defaultPm,
          loading: false,
          error
        }
      };
    }
    case SET_DEFAULT_CARD_END: {
      return {
        ...state,
        defaultPm: {
          ...state.defaultPm,
          loading: false
        }
      };
    }
    case GET_CHECKOUT_PAGE_SUCCESS: {
      const {
        customer_data: customer,
        bank_redirect_customer_payment_methods: bankRedirectList,
        customer_payment_methods: cardsList,
        payment_method_types: pmts
      } = action.payload.data || {};

      const card = (pmts || []).find(
        (pmt: ICategory) => pmt?.category === CATEGORIES.card
      )?.types;
      const bank = (pmts || []).find(
        (pmt: ICategory) => pmt?.category === CATEGORIES.bank
      )?.types;

      return {
        ...state,
        customerToken: customer?.id,
        pagePaymentMethodsTypes: {
          ...state.pagePaymentMethodsTypes,
          card,
          bank
        },
        customerPaymentMethods: {
          ...state.customerPaymentMethods,
          data: { card: cardsList, bank: bankRedirectList }
        }
      };
    }
    case GET_SUBSCRIPTION_PAGE_SUCCESS: {
      const {
        customer,
        bank_redirect_customer_payment_methods: bankRedirectList,
        customer_payment_methods: cardsList,
        payment_method_types: pmts
      } = action.payload.data || {};

      const card = (pmts || []).find(
        (pmt: ICategory) => pmt?.category === CATEGORIES.card
      )?.types;
      const bank = (pmts || []).find(
        (pmt: ICategory) => pmt?.category === CATEGORIES.bank
      )?.types;

      return {
        ...state,
        customerToken: customer?.id,
        pagePaymentMethodsTypes: {
          ...state.pagePaymentMethodsTypes,
          card,
          bank
        },
        customerPaymentMethods: {
          ...state.customerPaymentMethods,
          data: {
            ...state.customerPaymentMethods?.data,
            card: cardsList,
            bank: bankRedirectList
          }
        }
      };
    }
    default:
      return state;
  }
};

export default reducer;
